/**
 * `usePersistentPanelBadgeCount` — the number on the panel handle's badge
 * (#1920; the lead's count since #1921). The panel is closed by default, so
 * the badge is how a closed panel still tells the user something is
 * waiting; hidden at 0.
 *
 *   - **Lead** — the number of rows the next-steps list shows right now
 *     (`components/panel/useNextSteps`): one per kind of step (days short,
 *     leave to review, swaps to confirm, unpublished edits, the team not
 *     told, pins that can't be placed, messages from members, calls waiting, no schedules yet,
 *     submissions outstanding). It reads through the list's own hook, so it
 *     shares its cache entries and its gates. Validation is a secondary line
 *     of the short-days row, not a row, so it never counts — and its heavy
 *     validate-roster request is never fired for the closed badge (the hook
 *     only reads it while the panel is open).
 *   - **Member** — the number of rows `WhatNeedsYouSection` lists (teams
 *     with open schedules + one for upcoming shifts), via its own hook.
 *
 * Never throws: every read is `retry: false` with no error boundary, and
 * an errored or loading read counts as 0.
 */
import { useWhatNeedsYou } from "../components/WhatNeedsYouSection";
import { useNextSteps } from "../components/panel/useNextSteps";

export function usePersistentPanelBadgeCount(variant: "lead" | "member"): number {
  const lead = useNextSteps({ enabled: variant === "lead" }).rows.length;
  const member = useWhatNeedsYou({ enabled: variant === "member" }).itemCount;
  return variant === "lead" ? lead : member;
}
